/**
 * SendInfoSheetOnAssignDialog (URS-F495)
 *
 * Popup que aparece al asignar un voluntario a un estudio (desde el
 * panel Voluntarios del estudio o desde la ficha global del voluntario)
 * y ofrece enviar por email la(s) HIP(s) del estudio automaticamente.
 *
 * - 0 HIPs configuradas -> mensaje informativo (no bloquea).
 * - 1 HIP  -> confirmacion simple.
 * - >1 HIPs -> checkboxes con pre-seleccion por `send_by_email`.
 *
 * Endpoint: POST /api/studies/{sid}/info-sheets/send/{vid}
 *   Body:   { sheet_ids: [...], trigger: 'on_assignment' }
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Warning, Envelope, FileText } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LABELS = {
  es: {
    title: 'Enviar HIP al voluntario',
    loading: 'Cargando hojas informativas...',
    no_sheets: 'Este estudio no tiene HIPs configuradas todavía. No se enviará ningún email.',
    no_email: 'El voluntario no tiene email registrado. No se puede enviar la HIP por email.',
    single_intro: 'Se enviará la siguiente Hoja de Información al voluntario:',
    multi_intro: 'Selecciona qué Hoja(s) de Información enviar al voluntario:',
    volunteer: 'Voluntario',
    to_email: 'Email destino',
    cancel: 'Cancelar',
    close: 'Cerrar',
    send: 'Enviar por email',
    sending: 'Enviando...',
    sent_ok: 'HIP enviada correctamente',
    error_load: 'Error al cargar HIPs',
    error_send: 'Error al enviar la HIP',
    select_at_least_one: 'Selecciona al menos una HIP',
  },
  en: {
    title: 'Send Info Sheet to volunteer',
    loading: 'Loading info sheets...',
    no_sheets: 'This study has no info sheets configured yet. No email will be sent.',
    no_email: 'Volunteer has no email on file. Cannot send Info Sheet by email.',
    single_intro: 'The following Info Sheet will be sent to the volunteer:',
    multi_intro: 'Select which Info Sheet(s) to send to the volunteer:',
    volunteer: 'Volunteer',
    to_email: 'Destination email',
    cancel: 'Cancel',
    close: 'Close',
    send: 'Send by email',
    sending: 'Sending...',
    sent_ok: 'Info Sheet sent successfully',
    error_load: 'Error loading info sheets',
    error_send: 'Error sending Info Sheet',
    select_at_least_one: 'Select at least one Info Sheet',
  },
};

export default function SendInfoSheetOnAssignDialog({
  open,
  onClose,
  studyId,
  studyCode,
  volunteerId,
  volunteerName,
  volunteerEmail,
  lang = 'es',
}) {
  const lb = LABELS[lang] || LABELS.es;
  const [loading, setLoading] = useState(true);
  const [sheets, setSheets] = useState([]);
  const [selected, setSelected] = useState({}); // {sheet_id: bool}
  const [sending, setSending] = useState(false);

  // Load info sheets when opened
  useEffect(() => {
    if (!open || !studyId) return;
    let cancelled = false;
    setLoading(true);
    setSheets([]);
    setSelected({});
    axios
      .get(`${API_URL}/api/studies/${studyId}/info-sheets`, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        const list = (data && data.info_sheets) || [];
        setSheets(list);
        // Pre-seleccion: hojas con send_by_email !== false marcadas.
        const preset = {};
        for (const s of list) {
          preset[s.sheet_id] = s.send_by_email !== false;
        }
        setSelected(preset);
      })
      .catch(() => {
        if (!cancelled) toast.error(lb.error_load);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, studyId]);

  const hasEmail = !!(volunteerEmail && String(volunteerEmail).trim());
  const selectedIds = useMemo(
    () => Object.entries(selected).filter(([_, v]) => v).map(([k]) => k),
    [selected],
  );

  const canSend = !loading && sheets.length > 0 && hasEmail && selectedIds.length > 0 && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/studies/${studyId}/info-sheets/send/${volunteerId}`,
        { sheet_ids: selectedIds, trigger: 'on_assignment' },
        { withCredentials: true },
      );
      toast.success(lb.sent_ok);
      onClose?.(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || lb.error_send;
      toast.error(typeof msg === 'string' ? msg : lb.error_send);
    } finally {
      setSending(false);
    }
  };

  const toggle = (sheetId) => {
    setSelected(prev => ({ ...prev, [sheetId]: !prev[sheetId] }));
  };

  const isEmpty = !loading && sheets.length === 0;
  const isSingle = !loading && sheets.length === 1;
  const isMulti = !loading && sheets.length > 1;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !sending) onClose?.(false); }}>
      <DialogContent data-testid="send-info-sheet-dialog" className="max-w-lg overflow-hidden">
        <DialogHeader className="min-w-0">
          <DialogTitle className="flex items-center gap-2 min-w-0">
            <Envelope size={18} weight="regular" className="text-[#1f3e80] flex-shrink-0" />
            <span className="truncate">{lb.title}</span>
          </DialogTitle>
          {(volunteerName || volunteerEmail) && (
            <DialogDescription asChild>
              <div className="text-xs text-stone-600 space-y-0.5 pt-1">
                <div><span className="font-medium">{lb.volunteer}:</span> {volunteerName || volunteerId} {studyCode && <span className="text-stone-400">· {studyCode}</span>}</div>
                {hasEmail ? (
                  <div><span className="font-medium">{lb.to_email}:</span> <span className="font-mono">{volunteerEmail}</span></div>
                ) : (
                  <div className="text-red-600 flex items-center gap-1"><Warning size={14} /> {lb.no_email}</div>
                )}
              </div>
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="py-2 min-h-[80px] min-w-0">
          {loading && (
            <p className="text-sm text-stone-500 italic" data-testid="send-info-sheet-loading">{lb.loading}</p>
          )}

          {isEmpty && (
            <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2" data-testid="send-info-sheet-empty">
              <Warning size={16} className="mt-0.5 flex-shrink-0" />
              <span>{lb.no_sheets}</span>
            </div>
          )}

          {isSingle && (
            <div className="text-sm text-stone-700 space-y-2" data-testid="send-info-sheet-single">
              <p>{lb.single_intro}</p>
              <div className="flex items-center gap-2 rounded-sm border border-stone-200 bg-stone-50 px-3 py-2 min-w-0">
                <FileText size={16} className="text-[#1f3e80] flex-shrink-0" />
                <span className="font-medium flex-shrink-0">{sheets[0].label || 'HIP'}</span>
                <span className="text-xs text-stone-500 font-mono truncate min-w-0">{sheets[0].filename}</span>
              </div>
            </div>
          )}

          {isMulti && (
            <div className="text-sm text-stone-700 space-y-2 min-w-0" data-testid="send-info-sheet-multi">
              <p>{lb.multi_intro}</p>
              <div className="max-h-60 overflow-y-auto overflow-x-hidden rounded-sm border border-stone-200 divide-y divide-stone-100">
                {sheets.map(s => (
                  <label
                    key={s.sheet_id}
                    htmlFor={`hip-${s.sheet_id}`}
                    className="flex items-center gap-2 px-3 py-2 min-w-0 hover:bg-stone-50 cursor-pointer"
                    data-testid={`send-info-sheet-item-${s.sheet_id}`}
                  >
                    <Checkbox
                      id={`hip-${s.sheet_id}`}
                      checked={!!selected[s.sheet_id]}
                      onCheckedChange={() => toggle(s.sheet_id)}
                      className="flex-shrink-0"
                      data-testid={`send-info-sheet-check-${s.sheet_id}`}
                    />
                    <FileText size={14} className="text-[#1f3e80] flex-shrink-0" />
                    <span className="font-medium text-sm flex-shrink-0">{s.label || 'HIP'}</span>
                    <span className="text-xs text-stone-500 font-mono truncate flex-1 min-w-0">{s.filename}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onClose?.(false)}
            disabled={sending}
            data-testid="send-info-sheet-cancel"
          >
            {isEmpty ? lb.close : lb.cancel}
          </Button>
          {!isEmpty && (
            <Button
              onClick={handleSend}
              disabled={!canSend}
              className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white"
              data-testid="send-info-sheet-confirm"
            >
              {sending ? lb.sending : lb.send}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
